import React, { useEffect, useRef, useState } from 'react';
import { Modal, Field } from '../../components/ui';
import { INVITE_STATUSES, VISIBILITIES, fmtDate, fmtTime, normaliseText, nameInput, tenDigits, MAX_NAME } from '../../utils/helpers';
import { MAX_FAMILY_SIZE } from './useMembers';
import NumberChoice from './NumberChoice';
import PeopleSearch from './PeopleSearch';

const ten = tenDigits;

// Mobile (APK) guest form — built for adding MANY guests one after another on a
// phone: once the functions are chosen they collapse into one line (date +
// name) so the people fields start at the top, every person is Surname / Name /
// Mobile with a phone-book button beside them, "Save & add next" keeps the form
// open, and the rarely-used fields sit under "More details". Logic: useMembers.
export default function MemberFormMobile({ vm }) {
  const {
    pickerFunctions, form, setF, setMobile, editing, fnName, me, selectedFunctionIds, toggleFunction,
    allFunctionsSelected, toggleAllFunctions, addedThisSession,
    party, addParty, setPartyField, removeParty, togglePartyFunction, partyFunctionIds, familyMode,
    addMode, setAddMode, distinctPeople, dupes, blockedSameFn,
    canImport, importContact, importPartyContact, pickExisting, save, saving, setOpen,
    knownSurnames, knownNames,
    isMarriage, lockedSide,
  } = vm;
  const [more, setMore] = useState(!!editing);
  // The chips take half the screen; once something is chosen they fold away
  // into the summary line and "Change" brings them back.
  const [pickFns, setPickFns] = useState(selectedFunctionIds.length === 0);
  // Which member's function chips are open (one at a time keeps the form short).
  const [openFnFor, setOpenFnFor] = useState(null);
  const nameRef = useRef(null);
  // Each family-member name box, so a newly added row takes the keyboard
  // straight away: 8 relatives are typed without hunting for the next field.
  const partyRefs = useRef([]);
  const partyCount = useRef(party.length);

  // After "Save & add next" the name clears — put the cursor back there.
  useEffect(() => { if (!editing && nameRef.current && form.GuestName === '' && form.MobileNumber === '') nameRef.current.focus(); }, [addedThisSession]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (selectedFunctionIds.length === 0) setPickFns(true); }, [selectedFunctionIds.length]);
  useEffect(() => {
    if (party.length > partyCount.current) partyRefs.current[party.length - 1]?.focus();
    partyCount.current = party.length;
  }, [party.length]);

  const title = editing ? 'Edit Guest' : (familyMode ? 'Add Family' : 'Add Guest');
  // A phone number is optional - only its shape is checked when given.
  const mobileOk = !String(form.MobileNumber || '').trim() || /^\d{10}$/.test(String(form.MobileNumber));
  const canSave = !saving && !blockedSameFn && selectedFunctionIds.length > 0 && String(form.GuestName).trim() && mobileOk;

  // What the top line says: the chosen function with its date, or how many.
  const chosen = pickerFunctions.filter(f => selectedFunctionIds.includes(String(f.FunctionId)));
  const first = chosen[0];

  return (
    <Modal
      fullScreen
      title={addedThisSession ? `${title} · ${addedThisSession} added` : title}
      onClose={() => setOpen(false)}
      footer={editing ? (
        <>
          <button className="btn light" onClick={() => setOpen(false)}>Cancel</button>
          <button className="btn primary" style={{ flex: 1, padding: 12 }} disabled={saving || blockedSameFn} onClick={() => save()}>{saving ? 'Saving...' : 'Save'}</button>
        </>
      ) : (
        <>
          <button className="btn light" onClick={() => setOpen(false)}>Done</button>
          <button className="btn light" style={{ flex: 1, padding: 12 }} disabled={!canSave} onClick={() => save()}>
            {saving ? 'Saving...' : (familyMode ? 'Save family' : 'Save')}
          </button>
          <button className="btn primary" style={{ flex: 1.4, padding: 12 }} disabled={!canSave} onClick={() => save({ addNext: true })}>
            {saving ? 'Saving...' : 'Save & add next ➜'}
          </button>
        </>
      )}
    >
      {/* 1 · Which function(s) — one line once decided, chips to change it */}
      {first && !pickFns ? (
        <div className="fn-chosen" onClick={() => setPickFns(true)}>
          <div>
            <strong>{first.FunctionName}</strong>
            {chosen.length > 1 && <span className="muted"> +{chosen.length - 1} more</span>}
            <div className="muted" style={{ fontSize: 12 }}>
              {fmtDate(first.FunctionDate)}{first.StartTime ? ` · ${fmtTime(first.StartTime)}` : ''}{first.Venue ? ` · ${first.Venue}` : ''}
            </div>
          </div>
          <button type="button" className="btn light sm" onClick={(e) => { e.stopPropagation(); setPickFns(true); }}>Change</button>
        </div>
      ) : (
        <>
          <div className="step-label">Invite to {selectedFunctionIds.length ? `(${selectedFunctionIds.length} selected)` : '— tap the functions'}</div>
          <div className="chips-row" style={{ marginBottom: 12 }}>
            {pickerFunctions.length === 0 && <span className="muted" style={{ fontSize: 12 }}>All functions of this event are over — nothing to invite to.</span>}
            {pickerFunctions.map(f => {
              const on = selectedFunctionIds.includes(String(f.FunctionId));
              return (
                <button key={f.FunctionId} type="button" disabled={f.past}
                  className={'chip-toggle' + (on ? ' on' : '')} style={{ opacity: f.past ? 0.55 : 1 }}
                  onClick={() => toggleFunction(f.FunctionId)}>
                  {on ? '✓ ' : ''}{f.FunctionName} ({vm.fnCount(f.FunctionId)}){f.past ? ' (over)' : ''}
                </button>
              );
            })}
            {pickerFunctions.filter(f => !f.past).length > 1 && (
              <button type="button" className="chip-toggle ghost" onClick={toggleAllFunctions}>{allFunctionsSelected ? 'None' : 'All'}</button>
            )}
            {selectedFunctionIds.length > 0 && (
              <button type="button" className="chip-toggle ghost" onClick={() => setPickFns(false)}>✓ Done</button>
            )}
          </div>
        </>
      )}

      {/* 2 · Who — re-invite shortcut, then Surname / Name / Mobile */}
      {!editing && !familyMode && !!distinctPeople.length && (
        <button type="button" className={`btn ${addMode === 'existing' ? 'primary' : 'light'}`} style={{ padding: 11, width: '100%', marginBottom: 10 }}
          onClick={() => setAddMode(addMode === 'existing' ? 'new' : 'existing')}>
          {addMode === 'existing' ? '✕ Close list' : '↻ Already-added person'}
        </button>
      )}
      {!editing && !familyMode && addMode === 'existing' && (
        <Field label="Re-invite an existing guest to more functions" type="select" value=""
          options={distinctPeople.map(p => ({ value: p.GuestId, label: `${[p.Surname, p.GuestName, p.FatherHusband].filter(Boolean).join(' ')} · ${p.MobileNumber || ''}` }))}
          onChange={pickExisting} />
      )}

      {/* Someone already known (any event / synced contacts) → fills the fields. */}
      {!editing && (familyMode || addMode === 'new') && <PeopleSearch vm={vm} />}

      <div className="person-head">
        <strong style={{ fontSize: 13 }}>{familyMode ? 'Family head' : 'Guest'}</strong>
        <div style={{ flex: 1 }} />
        {/* The phone book fills surname, name and number in one tap. */}
        {canImport && !editing && (
          <button type="button" className="btn light sm" onClick={importContact}>📇 Phone book</button>
        )}
      </div>
      {/* The suggestions are the surnames / names already used in this event,
          so one family is spelt the same way every time. */}
      <datalist id="known-surnames">{knownSurnames.map(s => <option key={s} value={s} />)}</datalist>
      <datalist id="known-names">{knownNames.map(s => <option key={s} value={s} />)}</datalist>
      <label className="field">Surname
        <input type="text" list="known-surnames" value={form.Surname} placeholder="Surname" autoComplete="off" enterKeyHint="next"
          maxLength={MAX_NAME} style={{ fontSize: 16, padding: 12 }} onChange={(e) => setF('Surname', nameInput(e.target.value))} />
      </label>
      <label className="field">{familyMode ? 'Family head name' : 'Name'} *
        <input ref={nameRef} type="text" list="known-names" value={form.GuestName} placeholder="First name" autoComplete="off" enterKeyHint="next"
          maxLength={MAX_NAME} style={{ fontSize: 16, padding: 12 }} onChange={(e) => setF('GuestName', nameInput(e.target.value))} />
      </label>
      <label className="field">{familyMode ? 'Family head mobile' : 'Mobile number'}
        <input type="tel" inputMode="numeric" maxLength={10} value={form.MobileNumber} placeholder="10-digit mobile (optional)" autoComplete="off" enterKeyHint="done"
          style={{ fontSize: 18, padding: 12, letterSpacing: 1 }} onChange={(e) => setMobile(e.target.value)} />
      </label>

      {/* A business associate is invited as a firm; City is kept for everyone. */}
      <Field label="Guest is" type="select" value={form.GuestType || 'Family'}
        options={[{ value: 'Family', label: 'Family / friend' }, { value: 'Business', label: 'Business associate' }]}
        onChange={(v) => setF('GuestType', v || 'Family')} />
      {String(form.GuestType) === 'Business' && (
        <Field label="Firm / Company" value={form.CompanyName} placeholder="printed under the owner's name"
          onChange={(v) => setF('CompanyName', normaliseText(v))} />
      )}
      <Field label="City" value={form.City} onChange={(v) => setF('City', normaliseText(v))} />

      {!editing && dupes.length > 0 && (
        <div className="card card-pad" style={{ marginTop: 4, marginBottom: 10, background: blockedSameFn ? '#fee2e2' : '#fef3c7', fontSize: 13 }}>
          <strong>Already in this event:</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {dupes.map(d => (
              <li key={d.GuestId}>
                {(d.InvitedByName || '').trim() || 'Someone'} - {d.FunctionName || fnName(d.FunctionId)}{String(d.CreatedBy) === String(me) ? ' (you)' : ''}
              </li>
            ))}
          </ul>
          {blockedSameFn
            ? <div style={{ marginTop: 6, color: 'var(--danger)' }}>You already added this person to this function.</div>
            : <div style={{ marginTop: 6 }}>Another member invited them too — you can still add them to your list.</div>}
        </div>
      )}

      {/* Family members — same three fields, same phone book, per person */}
      {!editing && familyMode && (
        <div style={{ marginTop: 6, marginBottom: 10 }}>
          <div className="person-head">
            <strong style={{ fontSize: 13 }}>Family members</strong>
            <span className="muted" style={{ fontSize: 12 }}>{party.length + 1} incl. head · surname {form.Surname || '—'}</span>
            <div style={{ flex: 1 }} />
            <button type="button" className="btn sm light" onClick={addParty} disabled={party.length >= MAX_FAMILY_SIZE - 1}>+ Member</button>
          </div>
          {party.map((m, i) => (
            <div key={i} className="card card-pad" style={{ padding: 10, marginBottom: 8 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                <span className="muted" style={{ fontSize: 12 }}>{i + 2}.</span>
                <div style={{ flex: 1 }} />
                {canImport && <button type="button" className="btn light sm" onClick={() => importPartyContact(i)}>📇 Phone book</button>}
                <button type="button" className="btn danger sm" onClick={() => removeParty(i)}>✕</button>
              </div>
              {/* Name and number only: the member carries the head's surname. */}
              <input placeholder="Name" list="known-names" value={m.GuestName} maxLength={MAX_NAME}
                ref={el => { partyRefs.current[i] = el; }} enterKeyHint={i === party.length - 1 ? 'done' : 'next'}
                style={{ width: '100%', fontSize: 16, padding: 10, marginBottom: 6 }}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); partyRefs.current[i + 1]?.focus(); } }}
                onChange={e => setPartyField(i, 'GuestName', nameInput(e.target.value))} />
              <input placeholder="Mobile (optional)" type="tel" inputMode="numeric" maxLength={10} value={m.MobileNumber}
                style={{ width: '100%', fontSize: 15, padding: 10 }}
                onChange={e => setPartyField(i, 'MobileNumber', ten(e.target.value))} />
              {/* This person's own functions. Everyone follows the family by
                  default; untick to invite a child to the Haldi only. */}
              {chosen.length > 1 && (() => {
                const own = partyFunctionIds(m);
                const all = own.length === chosen.length;
                return (
                  <>
                    <button type="button" className="linklike" style={{ fontSize: 12, marginTop: 6 }}
                      onClick={() => setOpenFnFor(openFnFor === i ? null : i)}>
                      {all ? `All ${chosen.length} functions` : `${own.length} of ${chosen.length} functions`} {openFnFor === i ? '▴' : '▾'}
                    </button>
                    {openFnFor === i && (
                      <div className="chips-row" style={{ marginTop: 6 }}>
                        {chosen.map(f => (
                          <button key={f.FunctionId} type="button"
                            className={'chip-toggle' + (own.includes(String(f.FunctionId)) ? ' on' : '')}
                            onClick={() => togglePartyFunction(i, f.FunctionId)}>
                            {own.includes(String(f.FunctionId)) ? '✓ ' : ''}{f.FunctionName}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          ))}
        </div>
      )}

      {/* 3 · Side — never asked here: it is the user's own side (My Profile). */}
      {isMarriage && (
        <>
          <div className="step-label">Side</div>
          <div className="chips-row" style={{ marginBottom: 12 }}>
            <span className="chip-toggle on" aria-disabled="true">{lockedSide || form.Side}</span>
          </div>
        </>
      )}

      {/* 4 · Rarely used — collapsed */}
      <button type="button" className="linklike" style={{ fontSize: 13, marginBottom: 8 }} onClick={() => setMore(m => !m)}>
        {more ? '▾ Hide details' : '▸ More details (father/husband, visibility, remarks)'}
      </button>
      {more && (
        <div>
          {!familyMode && <Field label="Father / Husband name" value={form.FatherHusband} onChange={(v) => setF('FatherHusband', normaliseText(v))} />}
          <Field label="Visible to" type="select" value={form.Visibility}
            options={VISIBILITIES.map(v => ({ value: v, label: v === 'Private' ? 'Only me + admin (Private)' : 'Everyone (Common)' }))}
            onChange={(v) => setF('Visibility', v)} />
          {editing && (
            <>
              <Field label="Relation" value={form.Relation} onChange={(v) => setF('Relation', normaliseText(v))} />
              <Field label="Group" value={form.GroupName} onChange={(v) => setF('GroupName', normaliseText(v))} />
              <Field label="Invite Status" type="select" value={form.InviteStatus} options={INVITE_STATUSES} onChange={(v) => setF('InviteStatus', v)} />
              <Field label="Invited Via" type="select" value={form.InvitedVia} options={['WhatsApp', 'Call', 'Card']} onChange={(v) => setF('InvitedVia', v)} />
            </>
          )}
          <Field label="Remarks" type="textarea" rows={2} value={form.Remarks} onChange={(v) => setF('Remarks', v)} />
        </div>
      )}
      <NumberChoice vm={vm} />
    </Modal>
  );
}
